import React, { useEffect, useState } from 'react';
import { Field, useFormikContext } from 'formik';
import {
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { StepWrapper, StepperControls, StyledCardSection, DatePicker } from '@fnol/library/src/components';
import {
    ScreenProps,
    common,
    GenericObject,
    policyNoMaxLength,
    questionMaxLength,
    calendarYearsFromNow,
    getIsPortal,
    getIsWebsite,
    getIsCallCenter,
} from '@fnol/library/src/lib';
import { useApi } from '@fnol/library/src/hooks';
import { insuranceGroup, policiesProperties, translations } from '../../lib/consts';
import { preparePublicUtilitiesList, prepareUnionsList, scrollToFirstError } from '../../lib/helpers';
import {
    getActivePoliciesList,
    getInsuranceMethod,
    getPolicyDetails,
    getPublicUtilities,
    getUnions,
} from '../../rpc/actions';
import { ActivePoliciesSelect } from './ActivePoliciesSelect';
import './policy.css';

interface FormData {
    policyNumber: string;
    insuranceMethodList: Array<GenericObject>;
    insuranceMethod: number;
    groupInsuranceMethod: number;
    dateOfOccurance: string;
    policyNotFound: string;
    publicUtility: string;
    referenceNumber: string;
    insuranceConfirmationID: string;
    union: string;
    publicUtilities: Array<GenericObject>;
    unions: Array<GenericObject>;
    activePolicies: [GenericObject];
    activePolicyNumber: '';
    activePolicy: GenericObject;
    insuranceMethodInfo: string;
    insuredAddress: string;
    prefillSubmitterOwner: boolean;
    policyNumberAPI: string;
}

const PolicyDetailScreen = ({ navigator }: ScreenProps) => {
    const [currentStep, { goForward }] = navigator;
    const { values, errors, touched, setErrors, setTouched, setFieldValue, handleChange } =
        useFormikContext<FormData>();
    const [showInsuranceMethod, setShowInsuranceMethod] = useState<boolean>(false);

    const toDate = (date: string) => {
        return new Date(date);
    };

    let dataForDetails: GenericObject = {};

    const savePolicyDetails = (res: GenericObject) => {
        if (res.data.poruka === '') {
            if (values.policyNumberAPI !== res.data[policiesProperties.policyNumber]) {
                setFieldValue('prefillSubmitterOwner', true);
            }
            setFieldValue('policyNumberAPI', res.data[policiesProperties.policyNumber]); // policy number returned by API can be different than search criteria
            setFieldValue('insuredPersonUCN', res.data[policiesProperties.insuredPersonUCN]);
            setFieldValue('suggestedAmount', res.data[policiesProperties.suggestedAmount]);
            setFieldValue('firstClaim', res.data[policiesProperties.firstClaim]);
            setFieldValue('validFrom', res.data[policiesProperties.validFrom]);
            setFieldValue('validTo', res.data[policiesProperties.validTo]);
            setFieldValue('reportedClaims', res.data[policiesProperties.reportedClaims]);
            setFieldValue('insuredCity', res.data[policiesProperties.city]);
            setFieldValue('insuredAddress', res.data[policiesProperties.address]);
            setFieldValue('jbkProdukcija', res.data.jbkProdukcija);
            goForward();
        } else {
            setFieldValue('policyNotFound', res.data[policiesProperties.message]);
        }
    };

    const savePublicUtilities = (res: GenericObject) => {
        setFieldValue('publicUtilities', preparePublicUtilitiesList(res.data));
    };

    const saveUnions = (res: GenericObject) => {
        setFieldValue('unions', prepareUnionsList(res.data));
    };

    const saveInsuranceMethod = (res: GenericObject) => {
        setFieldValue('insuranceMethodList', res.data);
    };

    const { response: policyDetails, fetchMethod: fetchPolicyDetails } = useApi(
        () => getPolicyDetails(dataForDetails),
        savePolicyDetails,
    );

    const { response: publicUtilities, fetchMethod: fetchPublicUtilities } = useApi(
        () => getPublicUtilities(),
        savePublicUtilities,
    );
    const { response: unions, fetchMethod: fetchUnions } = useApi(() => getUnions(), saveUnions);

    const { response: insuranceMethodList, fetchMethod: fetchInsuranceMethod } = useApi(
        () => getInsuranceMethod(),
        saveInsuranceMethod,
    );

    const dataForList = {};

    const savePolicyList = (res: GenericObject) => {
        if (!res.data[0][policiesProperties.message] || res.data[0][policiesProperties.message] === '') {
            setFieldValue('activePolicies', res.data);
        } else {
            setFieldValue('policyNotFound', res.data[0][policiesProperties.message]);
        }
    };

    const { response: policiesList, fetchMethod: fetchActivePoliciesList } = useApi(
        () => getActivePoliciesList(dataForList),
        savePolicyList,
    );

    const appId = sessionStorage.getItem(`house_appID`);

    const handleNext = (): void => {
        setFieldValue('policyNotFound', '');
        setTouched(
            {
                insuranceMethod: true,
                dateOfOccurance: true,
                policyNumber: true,
                publicUtility: true,
                referenceNumber: true,
                insuranceConfirmationID: true,
                union: true,
                activePolicyNumber: true,
            },
            false,
        );

        const existingErrors: GenericObject = {};
        let valid = true;

        if (showInsuranceMethod && !values.insuranceMethod) {
            existingErrors.insuranceMethod = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }

        if (appId && getIsPortal(parseInt(appId))) {
            if (!values.activePolicyNumber) {
                existingErrors.activePolicyNumber = translations.validationMessages.REQUIRED_FIELD;
                valid = false;
            }
        } else {
            if (
                (values.groupInsuranceMethod === insuranceGroup.policyNumber ||
                    values.groupInsuranceMethod === insuranceGroup.policyNumberBasic) &&
                !values.policyNumber
            ) {
                existingErrors.policyNumber = translations.validationMessages.REQUIRED_FIELD;
                valid = false;
            }
            if (values.groupInsuranceMethod === insuranceGroup.directMail && !values.referenceNumber) {
                existingErrors.referenceNumber = translations.validationMessages.REQUIRED_FIELD;
                valid = false;
            }
            if (values.groupInsuranceMethod === insuranceGroup.union && !values.union) {
                existingErrors.union = translations.validationMessages.REQUIRED_FIELD;
                valid = false;
            }
            if (values.groupInsuranceMethod === insuranceGroup.publicUtility && !values.publicUtility) {
                existingErrors.publicUtility = translations.validationMessages.REQUIRED_FIELD;
                valid = false;
            }
            if (values.groupInsuranceMethod === insuranceGroup.mobileOrWebApp && !values.insuranceConfirmationID) {
                existingErrors.insuranceConfirmationID = translations.validationMessages.REQUIRED_FIELD;
                valid = false;
            }
        }

        if (!values.dateOfOccurance) {
            existingErrors.dateOfOccurance = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }

        setTimeout(() => {
            setErrors(existingErrors);
            scrollToFirstError(existingErrors);
        });

        if (valid) {
            dataForDetails = {
                DatumNastanka:
                    values.dateOfOccurance !== ''
                        ? `${toDate(values.dateOfOccurance).getFullYear()}-${(
                              '0' +
                              (toDate(values.dateOfOccurance).getMonth() + 1)
                          ).slice(-2)}-${('0' + toDate(values.dateOfOccurance).getDate()).slice(-2)}`
                        : values.dateOfOccurance,
            };
            if (appId && getIsPortal(parseInt(appId))) {
                dataForDetails[policiesProperties.insuranceMethodIDForBE] =
                    values.activePolicy[policiesProperties.insuranceMethodIDFromBE];
                dataForDetails['brPolisePotvrde'] = values.activePolicyNumber;
            } else {
                dataForDetails[policiesProperties.insuranceMethodIDForBE] = values.insuranceMethod;
                if (
                    values.groupInsuranceMethod === insuranceGroup.policyNumber ||
                    values.groupInsuranceMethod === insuranceGroup.policyNumberBasic
                ) {
                    dataForDetails['brPolisePotvrde'] = values.policyNumber;
                }
                if (values.groupInsuranceMethod === insuranceGroup.directMail) {
                    dataForDetails['pozivNaBrDirektnaPosta'] = values.referenceNumber;
                }
                if (values.groupInsuranceMethod === insuranceGroup.union) {
                    dataForDetails['sindikatID'] = values.union;
                }
                if (values.groupInsuranceMethod === insuranceGroup.publicUtility) {
                    dataForDetails['jkpID'] = values.publicUtility;
                }
                if (values.groupInsuranceMethod === insuranceGroup.mobileOrWebApp) {
                    dataForDetails['brPolisePotvrde'] = values.insuranceConfirmationID;
                }
            }
            fetchPolicyDetails();
        }
    };

    useEffect(() => {
        fetchInsuranceMethod(); // needed also for portal to fill values.groupInsuranceMethod

        if (appId && (getIsWebsite(parseInt(appId)) || getIsCallCenter(parseInt(appId)))) {
            //web site - SF
            setShowInsuranceMethod(true);
        } else {
            setShowInsuranceMethod(false);
            if (appId && getIsPortal(parseInt(appId))) {
                fetchActivePoliciesList();
            }
        }
    }, []);

    const handleChangeInsuranceMethod = (e: { target: { value: any } }): any => {
        setFieldValue('insuranceMethod', e.target.value);
        setFieldValue(
            'groupInsuranceMethod',
            values.insuranceMethodList.filter((x) => x.id === e.target.value)[0][translations.GROUP],
        );
        setFieldValue(
            'insuranceMethodInfo',
            values.insuranceMethodList
                .filter((insuranceMethod) => insuranceMethod.id === e.target.value)
                .map((insuranceMethod) => (insuranceMethod.infoPoruka ? insuranceMethod.infoPoruka : ''))[0],
        );

        setFieldValue('policyNumber', '');
        setFieldValue('publicUtility', '');
        setFieldValue('referenceNumber', '');
        setFieldValue('insuranceConfirmationID', '');
        setFieldValue('union', '');
    };

    const dateChange = () => {
        setFieldValue('policyNotFound', '');
    };

    const activePolicyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const activePolicy = values.activePolicies.filter((policy: GenericObject) => {
            return policy.brojPolise === e.target.value;
        });
        setFieldValue('activePolicyNumber', e.target.value);
        setFieldValue('activePolicy', activePolicy[0]);
        setFieldValue('policyNotFound', '');
        setFieldValue(
            'groupInsuranceMethod',
            values.insuranceMethodList.filter(
                (insuranceMethod) => insuranceMethod.id === activePolicy[0].nacinOsiguranjaID,
            )[0][translations.GROUP],
        );
    };

    useEffect(() => {
        fetchPublicUtilities();
        fetchUnions();
    }, []);

    return (
        <StepWrapper>
            <StyledCardSection>
                <Typography style={{ fontWeight: 'bold', fontSize: '1.1rem' }} variant="caption">
                    {translations.POLICY}
                </Typography>
                {showInsuranceMethod && (
                    <>
                        <FormHelperText error={false}>
                            <>{translations.INSURANCE_METHOD_INSTRUCTIONS_1}</>
                        </FormHelperText>
                        <FormControl error={!!(touched.insuranceMethod && errors.insuranceMethod)} required>
                            <InputLabel>{translations.INSURANCE_METHOD}</InputLabel>
                            <Select
                                id="insuranceMethod"
                                name="insuranceMethod"
                                value={values.insuranceMethod || ''}
                                label={translations.INSURANCE_METHOD}
                                onChange={handleChangeInsuranceMethod}
                            >
                                {values.insuranceMethodList?.map((option: GenericObject) => {
                                    return (
                                        <MenuItem key={option.id} value={option.id}>
                                            {option.naziv}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                            {touched.insuranceMethod && errors.insuranceMethod ? (
                                <FormHelperText error={true}>
                                    <>{errors.insuranceMethod}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>

                        {(values.groupInsuranceMethod === insuranceGroup.policyNumber ||
                            values.groupInsuranceMethod === insuranceGroup.policyNumberBasic) && (
                            <Tooltip
                                title={values.insuranceMethodInfo}
                                disableHoverListener={!values.insuranceMethodInfo}
                                placement="top-start"
                            >
                                <FormControl error={!!(touched.policyNumber && errors.policyNumber)}>
                                    <Field
                                        name="policyNumber"
                                        id="policyNumber"
                                        label={translations.POLICY_NUMBER}
                                        as={TextField}
                                        inputProps={{
                                            maxLength: policyNoMaxLength,
                                        }}
                                        error={!!(touched.policyNumber && errors.policyNumber)}
                                        required
                                    />
                                    {touched.policyNumber && errors.policyNumber ? (
                                        <FormHelperText error={true}>
                                            <>{errors.policyNumber}</>
                                        </FormHelperText>
                                    ) : null}
                                </FormControl>
                            </Tooltip>
                        )}

                        {values.groupInsuranceMethod === insuranceGroup.publicUtility && (
                            <Tooltip
                                title={values.insuranceMethodInfo}
                                disableHoverListener={!values.insuranceMethodInfo}
                                placement="top-start"
                            >
                                <FormControl>
                                    <InputLabel id="public-utility-label" required={true}>
                                        {translations.PUBLIC_UTILITY}
                                    </InputLabel>
                                    <Select
                                        id="publicUtility"
                                        labelId="public-utility-label"
                                        name="publicUtility"
                                        value={values.publicUtility || ''}
                                        label={translations.PUBLIC_UTILITY}
                                        onChange={handleChange}
                                    >
                                        {values.publicUtilities?.map((option: GenericObject) => {
                                            return (
                                                <MenuItem key={option.id} value={option.id}>
                                                    {option.value}
                                                </MenuItem>
                                            );
                                        })}
                                    </Select>
                                    {touched.publicUtility && errors.publicUtility ? (
                                        <FormHelperText error={true}>
                                            <>{errors.publicUtility}</>
                                        </FormHelperText>
                                    ) : null}
                                </FormControl>
                            </Tooltip>
                        )}

                        {values.groupInsuranceMethod === insuranceGroup.directMail && (
                            <Tooltip
                                title={values.insuranceMethodInfo}
                                disableHoverListener={!values.insuranceMethodInfo}
                                placement="top-start"
                            >
                                <FormControl>
                                    <Field
                                        name="referenceNumber"
                                        id="referenceNumber"
                                        label={translations.REFERENCE_NUMBER}
                                        as={TextField}
                                        inputProps={{
                                            maxLength: questionMaxLength,
                                        }}
                                        error={!!(touched.referenceNumber && errors.referenceNumber)}
                                        required={true}
                                    />
                                    {touched.referenceNumber && errors.referenceNumber ? (
                                        <FormHelperText error={true}>
                                            <>{errors.referenceNumber}</>
                                        </FormHelperText>
                                    ) : null}
                                </FormControl>
                            </Tooltip>
                        )}

                        {values.groupInsuranceMethod === insuranceGroup.mobileOrWebApp && (
                            <Tooltip
                                title={values.insuranceMethodInfo}
                                disableHoverListener={!values.insuranceMethodInfo}
                                placement="top-start"
                            >
                                <FormControl>
                                    <Field
                                        name="insuranceConfirmationID"
                                        id="insuranceConfirmationID"
                                        label={translations.INSURANCE_CONFIRMATION_ID}
                                        as={TextField}
                                        inputProps={{
                                            maxLength: questionMaxLength,
                                        }}
                                        error={!!(touched.insuranceConfirmationID && errors.insuranceConfirmationID)}
                                        required={true}
                                    />
                                    {touched.insuranceConfirmationID && errors.insuranceConfirmationID ? (
                                        <FormHelperText error={true}>
                                            <>{errors.insuranceConfirmationID}</>
                                        </FormHelperText>
                                    ) : null}
                                </FormControl>
                            </Tooltip>
                        )}

                        {values.groupInsuranceMethod === insuranceGroup.union && (
                            <Tooltip
                                title={values.insuranceMethodInfo}
                                disableHoverListener={!values.insuranceMethodInfo}
                                placement="top-start"
                            >
                                <FormControl>
                                    <InputLabel id="union-label" required={true}>
                                        {translations.UNION}
                                    </InputLabel>
                                    <Select
                                        id="union"
                                        labelId="union-label"
                                        name="union"
                                        value={values.union || ''}
                                        label={translations.UNION}
                                        onChange={handleChange}
                                    >
                                        {values.unions?.map((option: GenericObject) => {
                                            return (
                                                <MenuItem key={option.id} value={option.id}>
                                                    {option.value}
                                                </MenuItem>
                                            );
                                        })}
                                    </Select>
                                    {touched.union && errors.union ? (
                                        <FormHelperText error={true}>
                                            <>{errors.union}</>
                                        </FormHelperText>
                                    ) : null}
                                </FormControl>
                            </Tooltip>
                        )}
                    </>
                )}
                {values.activePolicies.length > 0 && (
                    <>
                        <FormHelperText error={false}>
                            <>{translations.POLICY_INSTRUCTIONS_PORTAL}</>
                        </FormHelperText>
                        <FormControl error={!!(touched.activePolicyNumber && errors.activePolicyNumber)} required>
                            <InputLabel id="activePolicyNumber">{translations.POLICY_SELECT_LABEL}</InputLabel>
                            <Field
                                name="activePolicyNumber"
                                id="activePolicyNumber"
                                label={translations.POLICY_SELECT_LABEL}
                                value={values.activePolicyNumber}
                                items={values.activePolicies}
                                onChange={activePolicyChange}
                                as={ActivePoliciesSelect}
                            />
                            {touched.activePolicyNumber && errors.activePolicyNumber ? (
                                <FormHelperText error={true}>
                                    <>{errors.activePolicyNumber}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>
                    </>
                )}
            </StyledCardSection>
            <StyledCardSection>
                <Typography style={{ fontWeight: 'bold', fontSize: '1.1rem' }} variant="caption">
                    {translations.DATE_OF_OCCURANCE}
                </Typography>
                <FormControl error={!!(touched.dateOfOccurance && errors.dateOfOccurance)} className="policyDatePicker">
                    <Field
                        name="dateOfOccurance"
                        id="dateOfOccurance"
                        label={translations.DATE}
                        as={DatePicker}
                        disableFuture
                        minDate={calendarYearsFromNow(3, true)}
                        error={!!(touched.dateOfOccurance && errors.dateOfOccurance)}
                        onChangeDate={dateChange}
                    />
                    {touched.dateOfOccurance && errors.dateOfOccurance ? (
                        <FormHelperText error={true}>
                            <>{errors.dateOfOccurance}</>
                        </FormHelperText>
                    ) : null}
                </FormControl>
            </StyledCardSection>
            <>
                {values.policyNotFound !== '' && (
                    <FormHelperText error={true}>
                        <>{values.policyNotFound}</>
                    </FormHelperText>
                )}
            </>
            <StepperControls onNext={handleNext} onNextLabel={common.NEXT} idNext="policyDetailScreenNext" />
        </StepWrapper>
    );
};

export default PolicyDetailScreen;
